import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/lib/api';
import { Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(username, password, displayName || username);
      navigate('/tasks');
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-zinc-950">
        <img src="https://static.prod-images.emergentagent.com/jobs/89959381-8fb5-45c9-9dbb-0dfb477e8d4d/images/9c9ca0bebd2db0e52e7a7cdf0a1a5f970f505e22cbcc72fb52a8c11be9c6bb5d.png"
          alt="Abstract" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <h1 className="text-5xl font-semibold text-white tracking-tight">Nexus</h1>
          <p className="text-zinc-400 mt-3 text-lg">Manage tasks, expenses, and more</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight lg:hidden">Nexus</h1>
            <h2 className="text-2xl font-medium tracking-tight mt-2">Create account</h2>
            <p className="text-sm text-zinc-500 mt-1">Get started with your workspace</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input id="username" value={username} onChange={e => setUsername(e.target.value)}
                placeholder="Choose a username" className="h-12" required data-testid="register-username" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input id="displayName" value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder="Your display name" className="h-12" data-testid="register-display-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 6 characters" className="h-12" required data-testid="register-password" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password</Label>
              <Input id="confirm" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password" className="h-12" required data-testid="register-confirm" />
            </div>
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
            </Button>
          </form>

          <p className="text-sm text-center text-zinc-500">
            Already have an account?{' '}
            <Link to="/login" className="text-zinc-900 dark:text-zinc-100 font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
